import { useState, useEffect, useCallback, useRef } from 'react';

export function useWebSocket({ 
  apiUrl, 
  onNewStop, 
  onRouteReordered, 
  onDriverLocation,
  enabled = true 
}) {
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);

  const connectWebSocket = useCallback(() => {
    if (!enabled) return;
    
    const wsProtocol = apiUrl.startsWith('https') ? 'wss:' : 'ws:';
    const wsHost = apiUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setWsConnected(true);
        if (wsReconnectRef.current) {
          clearTimeout(wsReconnectRef.current);
          wsReconnectRef.current = null;
        }
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'NEW_STOP' && onNewStop) {
            onNewStop(message);
          }
          
          if (message.type === 'ROUTE_REORDERED' && onRouteReordered) {
            onRouteReordered(message);
          }
          
          if (message.type === 'DRIVER_LOCATION' && onDriverLocation) {
            onDriverLocation(message.data);
          }
        } catch (e) {
          // WebSocket message parse error - non-critical
        }
      };
      
      ws.onclose = () => {
        setWsConnected(false);
        wsRef.current = null;
        
        wsReconnectRef.current = setTimeout(() => {
          connectWebSocket();
        }, 3000);
      };
      
      ws.onerror = () => {
        // WebSocket error - will trigger onclose for reconnection
      };
      
      wsRef.current = ws;
    } catch (e) {
      // WebSocket connection failed - retry after delay
      wsReconnectRef.current = setTimeout(connectWebSocket, 5000);
    }
  }, [apiUrl, onNewStop, onRouteReordered, onDriverLocation, enabled]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const sendLocationUpdate = useCallback((location, username = 'user') => {
    sendMessage({
      type: 'LOCATION_UPDATE',
      data: {
        username,
        lat: location.lat,
        lng: location.lng
      }
    });
  }, [sendMessage]);

  useEffect(() => {
    if (enabled) {
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (wsReconnectRef.current) {
        clearTimeout(wsReconnectRef.current);
      }
    };
  }, [enabled, connectWebSocket]);

  return {
    wsConnected,
    sendMessage,
    sendLocationUpdate
  };
}
